// Groups: a standing room (Matt, 2026-10-16: "set up a room for my family
// and then every day we would all be able to play that day's crossword by
// just hopping into the room"). A group is a name, one permanent code and a
// member list; each day it points at that day's ordinary room, which the
// first member to open it creates - so everything about solving together
// (rooms.js) is unchanged, a group just finds the room for you.
//
//   crosswordGroups/<id>/meta          { name, code, createdBy, createdAt,
//                                        members: { uid: { name, color, joinedAt } } }
//   crosswordGroups/<id>/days/<date>   roomId (create-only: one room a day)
//   crosswordGroupCodes/<CODE>         groupId (create-only)
//   crosswordUserGroups/<uid>/<id>     true - my groups, for the home screen
//
// A member keeps their colour in every day's room. Each day's room starts
// empty with its own clock, whatever anyone did alone first.
import {
  ref as dbRef, get, set, update, push, onValue, serverTimestamp,
} from 'firebase/database';
import { db } from '@/firebase';
import { generateRoomCode, normalizeRoomCode } from '@/utils/roomCodes';
import { SOLVER_COLORS, roomClockStart } from '@/engine/solving';
import { ROOMS_PATH, CODES_PATH, claimRoomCode } from '@/store/rooms';
import { isGiantId } from '@/engine/seasons';

export const GROUPS_PATH = 'crosswordGroups';
export const GROUP_CODES_PATH = 'crosswordGroupCodes';
export const USER_GROUPS_PATH = 'crosswordUserGroups';

const nameOf = (user) => user.displayName || user.email || 'Someone';

/** The first colour nobody in `members` has; round again past six. */
export function freeColor(members) {
  const taken = new Set(Object.values(members || {}).map((m) => m?.color));
  return SOLVER_COLORS.find((c) => !taken.has(c))
    || SOLVER_COLORS[Object.keys(members || {}).length % SOLVER_COLORS.length];
}

/** A trimmed, sensible group name. */
export function cleanGroupName(input) {
  return String(input || '').replace(/\s+/g, ' ').trim().slice(0, 30);
}

// Claim a group code that no room uses either, so one join box serves both.
async function claimGroupCode(groupId, preferred) {
  if (preferred) {
    try {
      await set(dbRef(db, `${GROUP_CODES_PATH}/${preferred}`), groupId);
      return preferred;
    } catch { /* another group has it; pick a fresh one */ }
  }
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const candidate = generateRoomCode(attempt);
    // eslint-disable-next-line no-await-in-loop -- one try at a time, in order
    if ((await get(dbRef(db, `${CODES_PATH}/${candidate}`))).exists()) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await set(dbRef(db, `${GROUP_CODES_PATH}/${candidate}`), groupId);
      return candidate;
    } catch { /* taken; try another */ }
  }
  throw new Error('Could not claim a group code.');
}

/**
 * Start a group. With `fromRoom` (the room I'm in, and its meta) the room's
 * solvers become the members with their colours, its code becomes the
 * group's if free, and it counts as the group's room for its day.
 */
export async function createGroup({ name, user, fromRoom }) {
  if (!db) throw new Error('Groups need Firebase configured.');
  const groupId = push(dbRef(db, GROUPS_PATH)).key;
  const code = await claimGroupCode(groupId, fromRoom?.meta?.code);
  const members = {};
  for (const [uid, p] of Object.entries(fromRoom?.meta?.players || {})) {
    members[uid] = { name: p.name || 'Someone', color: p.color || freeColor(members), joinedAt: p.joinedAt || Date.now() };
  }
  if (!members[user.uid]) members[user.uid] = { name: nameOf(user), color: freeColor(members), joinedAt: Date.now() };
  const patch = {
    [`${GROUPS_PATH}/${groupId}/meta`]: {
      name, code, createdBy: user.uid, createdAt: serverTimestamp(), members,
    },
  };
  // Everyone in it gets the card on their home screen, not just me.
  for (const uid of Object.keys(members)) patch[`${USER_GROUPS_PATH}/${uid}/${groupId}`] = true;
  if (fromRoom) {
    patch[`${GROUPS_PATH}/${groupId}/days/${fromRoom.meta.date}`] = fromRoom.roomId;
    patch[`${ROOMS_PATH}/${fromRoom.roomId}/meta/groupId`] = groupId;
    patch[`${ROOMS_PATH}/${fromRoom.roomId}/meta/groupName`] = name;
    patch[`${ROOMS_PATH}/${fromRoom.roomId}/meta/code`] = code;
  }
  await update(dbRef(db), patch);
  return { groupId, code };
}

/** Resolve a code to a group id, or null. */
export async function findGroupByCode(input) {
  const code = normalizeRoomCode(input);
  if (!code || !db) return null;
  const snap = await get(dbRef(db, `${GROUP_CODES_PATH}/${code}`));
  return snap.exists() ? { groupId: snap.val(), code } : null;
}

/** Add me to the group (idempotent). Returns the group's meta. */
export async function joinGroup(groupId, user) {
  const snap = await get(dbRef(db, `${GROUPS_PATH}/${groupId}/meta`));
  if (!snap.exists()) throw new Error('That group no longer exists.');
  const meta = snap.val();
  const patch = { [`${USER_GROUPS_PATH}/${user.uid}/${groupId}`]: true };
  if (!meta.members?.[user.uid]) {
    const mine = { name: nameOf(user), color: freeColor(meta.members), joinedAt: Date.now() };
    patch[`${GROUPS_PATH}/${groupId}/meta/members/${user.uid}`] = mine;
    meta.members = { ...meta.members, [user.uid]: mine };
  }
  await update(dbRef(db), patch);
  return meta;
}

/** Drop the group from my list and me from its members. */
export function leaveGroup(groupId, uid) {
  return update(dbRef(db), {
    [`${USER_GROUPS_PATH}/${uid}/${groupId}`]: null,
    [`${GROUPS_PATH}/${groupId}/meta/members/${uid}`]: null,
  });
}

/**
 * The group's room for `date` (a day, or a Giant's id), made now if nobody has yet. The day pointer
 * and the room are written in one atomic update and the pointer is
 * create-only, so two members opening at once get one room: the loser's
 * write is refused and they join the winner's.
 */
export async function roomForDay(groupId, group, date, user) {
  const dayRef = dbRef(db, `${GROUPS_PATH}/${groupId}/days/${date}`);
  const existing = await get(dayRef);
  if (existing.exists()) return existing.val();
  const roomId = push(dbRef(db, ROOMS_PATH)).key;
  const color = group.members?.[user.uid]?.color || SOLVER_COLORS[0];
  try {
    await update(dbRef(db), {
      [`${GROUPS_PATH}/${groupId}/days/${date}`]: roomId,
      [`${ROOMS_PATH}/${roomId}/meta`]: {
        date,
        code: group.code,
        groupId,
        groupName: group.name,
        hostUid: user.uid,
        createdAt: serverTimestamp(),
        startedAt: Date.now(),
        ...roomClockStart(0, Date.now()),
        solvedAt: null,
        players: { [user.uid]: { name: nameOf(user), color, joinedAt: serverTimestamp() } },
      },
    });
    // A Giant's room lasts the season and gets a code of its own: the
    // group's code opens the day's puzzle, which is not where a friend
    // following the link should land.
    if (isGiantId(date)) {
      const code = await claimRoomCode(roomId);
      await set(dbRef(db, `${ROOMS_PATH}/${roomId}/meta/code`), code);
    }
    return roomId;
  } catch (error) {
    const again = await get(dayRef);
    if (again.exists()) return again.val();
    throw error;
  }
}

/** My group ids, live. Returns an unbind function. */
export function watchMyGroups(uid, onIds) {
  return onValue(dbRef(db, `${USER_GROUPS_PATH}/${uid}`), (s) => onIds(Object.keys(s.val() || {})), () => onIds([]));
}

/**
 * One group's card, live: its meta, and today's room (meta + who's in it).
 * `onChange({ meta, room, present })`. Returns an unbind function.
 */
export function watchGroupDay(groupId, date, onChange) {
  const view = { meta: null, room: null, present: 0 };
  const emit = () => onChange({ ...view });
  let roomOffs = [];
  const offMeta = onValue(dbRef(db, `${GROUPS_PATH}/${groupId}/meta`), (s) => { view.meta = s.val(); emit(); }, () => {});
  const offDay = onValue(dbRef(db, `${GROUPS_PATH}/${groupId}/days/${date}`), (s) => {
    roomOffs.forEach((off) => off());
    roomOffs = [];
    view.room = null;
    view.present = 0;
    const roomId = s.val();
    if (roomId) {
      roomOffs = [
        onValue(dbRef(db, `${ROOMS_PATH}/${roomId}/meta`), (m) => { view.room = m.val(); emit(); }, () => {}),
        onValue(dbRef(db, `${ROOMS_PATH}/${roomId}/presence`), (p) => { view.present = Object.keys(p.val() || {}).length; emit(); }, () => {}),
      ];
    }
    emit();
  }, () => {});
  return () => { offMeta(); offDay(); roomOffs.forEach((off) => off()); };
}

